import { zodResolver } from "@hookform/resolvers/zod"
import { InfoIcon, Loader2Icon, TriangleAlertIcon } from "lucide-react"
import * as React from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { canSetCounter, CompanyCountersSchema } from "@steelbill/types"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { useAuth } from "@/features/auth/auth-context"
import { useSaveCounters, type CompanySettings } from "@/features/settings/use-company"
import { ApiError } from "@/lib/api-error"

/**
 * §6.1's counter override — "the escape hatch, not the main road".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ITS OWN FILE BECAUSE IT HAS ITS OWN AUDIENCE. It used to be a private
 * component inside `SettingsScreen.tsx`, and that placement — not the gate
 * written on it — was the bug: the card correctly checks
 * `invoice.set_counter`, but it could only be reached through `/settings`,
 * which `routes/index.tsx` guards with `settings.write`. The OWNER holds the
 * first and not the second (§2.1, `permissions.ts`), so the one person §6.1
 * built this for was redirected Home before the card ever rendered. That is the
 * exact collapse §6.1 forbids, achieved by routing rather than by the
 * capability map.
 *
 * So it lives here and mounts in TWO places, one component either way:
 *   - `CountersScreen` at `/invoices/numbers`, gated on `invoice.set_counter` —
 *     the owner's door, and what the search palette points at.
 *   - `SettingsScreen`, where the ADMIN already is when he is setting the
 *     business up.
 * Two mount points, ONE implementation: a second copy of this form is how the
 * confirmation switch or the direction warning goes missing from one of them.
 *
 * THE INTERNAL `can()` CHECK STAYS even though the route now carries the same
 * gate. §2.1's rule is that a component draws only what the signed-in person
 * may act on, and this one is rendered inside a screen whose gate is a
 * DIFFERENT capability.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * One field's validation message, or nothing.
 *
 * Written once because there are several fields now: a sequence typed as
 * anything the schema refuses would otherwise just disable the button with no
 * reason given — the shape of "the app ignored my tap" that this screen has
 * already produced once.
 */
function FieldError({ message }: { message?: unknown }): React.JSX.Element | null {
  if (message === undefined) return null
  return <p className="text-destructive text-xs">{String(message)}</p>
}

export function CountersCard({
  company,
}: {
  company: CompanySettings
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const { can, user } = useAuth()
  const save = useSaveCounters()

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * THE SERVER'S WARNINGS, HELD ON SCREEN — NOT DROPPED, AND NOT DRESSED AS
   * SUCCESS.
   *
   * `PUT /company/counters` answers 200 with `warnings: string[]`, composed by
   * `counterWarnings` in `routes/company.ts`. Each one is a consequence the
   * endpoint deliberately does NOT refuse — it is legal, and §5's switch-over
   * from the hand-written book needs it — but which the ADMIN has to know
   * happened: "next_invoice_seq was lowered from 67 to 60. Invoice numbers
   * between 60 and 66 will be issued a SECOND time — under GST a duplicate
   * invoice number is a compliance failure."
   *
   * That sentence was being thrown away and replaced with a green
   * "The numbers have been changed." A 200 with warnings is not a success, and
   * on THIS form the difference is the one thing §5 guarantees.
   *
   * A TOAST WOULD NOT BE ENOUGH, so this is component state feeding an inline
   * `Alert`. The backlog's toast rule is the reason: if the owner must ACT on
   * it, it is inline and persistent, not a corner that fades. He may need to
   * re-read this while deciding whether to move the counter back, or to tell
   * the CA — a Sonner toast is gone by then, and a duplicated invoice number is
   * not something he can discover later by looking at the counter, which simply
   * looks like a counter.
   *
   * It clears on the NEXT submit only, never on a timer: whatever is on screen
   * always describes the last change actually made.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const [warnings, setWarnings] = React.useState<readonly string[]>([])

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * AND THE REFUSALS, ON THE SAME TERMS. A 403 WAS A FOUR-SECOND TOAST.
   *
   * §6.1 lets the OWNER hold `invoice.set_counter` and still refuses him a
   * BACKWARD move, and `refuseBackwardMove` in `routes/company.ts` answers with
   * a four-sentence English paragraph — the rule, the consequence, the
   * alternative (`POST /invoices/record-manual`), and who can do it instead.
   * All of that was going to `toast.error` and nothing else: default duration
   * 4000ms, no hover-pause on a phone, gone before it can be read, nothing left
   * on screen and no field marked.
   *
   * The result was exactly backwards. A 200-with-warnings — which he merely has
   * to NOTE — got a persistent inline Alert, while the 403 he must ACT on
   * faded. The backlog's rule is one line: if the owner must act on it, it is an
   * inline Alert, not a toast. This is that.
   *
   * The toast stays as well, because it is what says "your tap did something"
   * at the instant of the tap; the Alert is what is still there afterwards.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const [saveError, setSaveError] = React.useState<string | null>(null)

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * WHERE THE EYE IS WHEN EITHER OF THOSE APPEARS — WHICH IS NOT HERE.
   *
   * Both boxes render at the TOP of the card. The submit button is at the
   * bottom, after the info box, three field groups, the reason input and the
   * confirmation switch. On the phone this screen is built for (§11) the owner
   * has scrolled past all of it to reach the button, so the Alert is inserted
   * well above the fold and the only thing he actually sees is a toast in the
   * bottom-right reading "read the warnings on the screen" — pointing at
   * something that is not on the screen.
   *
   * So the box is scrolled to and FOCUSED. `tabIndex={-1}` makes it focusable
   * without putting it in the tab order, and focusing rather than only
   * scrolling matters twice over: a screen reader lands on it, and the next Tab
   * continues from the warning instead of from wherever the button was.
   *
   * `scrollIntoView` is optional-called because jsdom does not implement it —
   * the tests that assert these sentences are rendered would otherwise throw
   * inside an effect, which is a test failure that says nothing about the app.
   * ═══════════════════════════════════════════════════════════════════════════
   */
  const attentionRef = React.useRef<HTMLDivElement | null>(null)
  React.useEffect(() => {
    if (warnings.length === 0 && saveError === null) return
    const node = attentionRef.current
    if (node === null) return
    node.scrollIntoView?.({ behavior: "smooth", block: "start" })
    node.focus()
  }, [warnings, saveError])

  /*
   * "May this person move a counter BACKWARD?" — the one question the two
   * sentences below actually turn on, answered by the same function the API
   * answers it with rather than by naming a role (§2.1, and see the Alert).
   *
   * `false` without a user, which is deny-by-default: an unauthenticated render
   * should show the more cautious wording, not the permissive one.
   */
  const mayMoveBackward = user === null ? false : canSetCounter(user.role, "backward")

  const form = useForm({
    resolver: zodResolver(CompanyCountersSchema),
    mode: "onTouched",
    defaultValues: {
      nextInvoiceSeq: company.nextInvoiceSeq,
      nextQuoteSeq: company.nextQuoteSeq,
      /*
       * THE CHALLAN SERIES, ADDED 2026-08-17 — THE SEQUENCE ONLY.
       *
       * The endpoint has accepted `nextChallanSeq` since the §17.7 series
       * shipped; only this form was behind, so the recovery
       * `services/numbering.ts` names in two comments — "change the counter on
       * the numbers screen" — meant an UPDATE typed into MariaDB on the live box.
       *
       * NO `*_fy` FIELD IS REGISTERED HERE, for any series. The markers roll
       * themselves on the first document of a new financial year, so a control
       * for them could only restate the present or break the future — see the
       * block above the fields. `currentFy` in particular is the INVOICE series'
       * marker: anything on this screen writing it would restart invoice
       * numbering, silently, every April, which is exactly when nobody is
       * looking. Not registering it is a stronger guarantee than remembering not
       * to send it.
       */
      nextChallanSeq: company.nextChallanSeq,
      reason: "",
      confirm: false as unknown as true,
    },
  })

  if (!can("invoice.set_counter")) return null

  const onSubmit = form.handleSubmit((values) => {
    /*
     * ═══════════════════════════════════════════════════════════════════════
     * ONLY THE COUNTERS THE OWNER ACTUALLY TOUCHED ARE SENT. SENDING THEM ALL
     * RE-ISSUES INVOICE NUMBERS THAT HAVE ALREADY GONE OUT.
     *
     * `defaultValues` is a snapshot taken when this card first rendered. The
     * screen then sits open — it is the sort of screen somebody opens, reads
     * twice and thinks about. Meanwhile bills get raised, on this machine or
     * his son's, and `next_invoice_seq` advances past the snapshot.
     *
     * Submitting the WHOLE form then wrote that stale number back. Change only
     * the challan counter, save, and the invoice series silently rewinds to
     * where it stood when the page loaded — and the next bill re-uses a number
     * that is already printed on paper in a customer's file. §5's gap-free
     * guarantee, broken by a field nobody typed in.
     *
     * `dirtyFields` is react-hook-form's record of what the USER changed, so an
     * untouched box sends nothing and the server's `?? previous.x` keeps
     * whatever the counter has since become. `CompanyCountersSchema` is built
     * for exactly this: every counter is optional and a `superRefine` only
     * insists that AT LEAST ONE is named.
     *
     * `reason` and `confirm` always ride along — they are not counters, the
     * server requires both, and they are typed fresh every time by definition.
     * ═══════════════════════════════════════════════════════════════════════
     */
    const dirty = form.formState.dirtyFields as Partial<Record<string, boolean>>
    const body: Record<string, unknown> = {
      reason: values.reason,
      confirm: values.confirm,
    }
    for (const [field, value] of Object.entries(values)) {
      if (field === "reason" || field === "confirm") continue
      if (dirty[field] === true) body[field] = value
    }

    // Whatever is on screen must describe the change that is on screen. The
    // previous run's warnings go before this one is sent, not after it answers.
    setWarnings([])
    setSaveError(null)

    save.mutate(body, {
      onSuccess: (result) => {
        /*
         * `warnings` is optional on the envelope and absent means NONE — the
         * route always computes the array and `res.json` drops it only when it
         * is empty. Defaulting to `[]` rather than to "assume the worst" is
         * therefore reading the contract, not shrugging at it.
         */
        const raised = result.warnings ?? []
        setWarnings(raised)

        // A warning is not a success, and the toast must not say otherwise.
        // The sentences themselves live in the Alert below — repeating each one
        // here as its own toast (as SettingsScreen does for the company row)
        // would stack four of them over the form they are about.
        if (raised.length > 0) toast.warning(t("counters.savedWithWarnings"))
        else toast.success(t("counters.saved"))

        form.reset({
          nextInvoiceSeq: form.getValues("nextInvoiceSeq"),
          nextQuoteSeq: form.getValues("nextQuoteSeq"),
          nextChallanSeq: form.getValues("nextChallanSeq"),
          reason: "",
          confirm: false as unknown as true,
        })
      },
      onError: (error) => {
        const message = error instanceof ApiError ? error.message : t("err.somethingWentWrong")
        setSaveError(message)
        toast.error(message)
      },
    })
  })

  const confirm = form.watch("confirm")

  /*
   * ═══════════════════════════════════════════════════════════════════════════
   * WHICH BOX IS THE ONE THE SERVER WILL REFUSE — ASKED ON THE CLIENT, DECIDED
   * ON THE SERVER.
   *
   * This is a HINT AND NOTHING MORE, and the distinction is load-bearing. It
   * does not disable the button, it does not block the submit and it is not a
   * resolver rule: `company.nextInvoiceSeq` is a snapshot from when the card
   * mounted, and bills get raised while this screen sits open, so a client-side
   * refusal built on it would eventually forbid a move the API allows. The
   * server holds the row under `SELECT ... FOR UPDATE` and compares against what
   * is ACTUALLY stored (routes/company.ts); that is the only answer that counts,
   * and nothing here changes what it allows or what it writes.
   *
   * What it buys is the sentence `counters.onlyForward` promises — "check the
   * numbers marked below" — actually having something marked. The refusal names
   * `next_invoice_seq` in English prose; a red line under the box he typed in is
   * what turns that into a thing he can fix.
   *
   * `mayMoveBackward` is the same capability question the API decides with, so
   * the ADMIN, for whom a lower number is legal, is never marked.
   */
  const typed = form.watch()
  const dirtyNow = form.formState.dirtyFields as Partial<Record<string, boolean>>

  function isLowered(field: "nextInvoiceSeq" | "nextQuoteSeq" | "nextChallanSeq"): boolean {
    if (mayMoveBackward) return false
    if (dirtyNow[field] !== true) return false
    const value = Number(typed[field])
    return Number.isFinite(value) && value < company[field]
  }

  const movingBackward =
    isLowered("nextInvoiceSeq") || isLowered("nextQuoteSeq") || isLowered("nextChallanSeq")

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("counters.title")}</CardTitle>
        <CardDescription>{t("counters.intro")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4">
          {/*
            * ═══════════════════════════════════════════════════════════════════
            * WHAT THE SERVER SAID ABOUT THE CHANGE THAT WAS JUST MADE.
            *
            * FIRST in the card, above the explanatory box and the fields, and
            * `variant="destructive"` — the same red the app uses for a load or
            * save failure. That is deliberate: the write SUCCEEDED, so a person
            * skimming has no other cue that anything needs reading, and the one
            * thing this screen can do wrong is invisible afterwards. Colour is
            * what carries urgency in this app (the toasts sit bottom-right for
            * exactly that reason), so a consequence the ADMIN must act on gets
            * the colour that stops him.
            *
            * `role="alert"` comes from `Alert` itself, so a screen reader
            * announces it when it appears rather than on the next focus move.
            *
            * THE SENTENCES ARE THE SERVER'S, VERBATIM AND IN ENGLISH. They name
            * real column names and real numbers ("next_invoice_seq was lowered
            * from 67 to 60"), and translating them here would mean a second
            * copy of §5's reasoning in two locales, drifting from the one the
            * API sends and the one the audit log records. The framing around
            * them IS translated, and says plainly that the lines are English —
            * an owner reading Marathi should be told that, not left to wonder
            * whether the app broke.
            * ═══════════════════════════════════════════════════════════════════
            */}
          {/*
            * ONE FOCUSABLE REGION HOLDING BOTH, so the effect above has a single
            * thing to scroll to and land on whichever of the two appeared. It
            * renders nothing at all when there is neither — an empty focusable
            * div at the top of the form would be a tab stop that says nothing.
            */}
          {warnings.length === 0 && saveError === null ? null : (
            <div ref={attentionRef} tabIndex={-1} className="flex flex-col gap-4 outline-none">
              {saveError === null ? null : (
                <Alert variant="destructive">
                  <TriangleAlertIcon />
                  <AlertTitle>
                    {movingBackward ? t("counters.onlyForward") : t("err.couldNotSave")}
                  </AlertTitle>
                  <AlertDescription>
                    <div className="flex flex-col gap-2">
                      {/*
                        * THE SERVER'S SENTENCE, VERBATIM AND IN ENGLISH — same
                        * decision as the warnings below, and for the same
                        * reason: it names the endpoint and the columns the
                        * audit log records, and a second translated copy of
                        * §6.1's reasoning would drift from the one the API
                        * sends. The framing says it is English so a Marathi
                        * reader is told, not left wondering.
                        */}
                      <p>{t("counters.errorEnglishNote")}</p>
                      <p>{saveError}</p>
                      {/*
                        * "No" with no next step is what makes somebody edit the
                        * database by hand — the refusal itself says so. The
                        * OWNER's way through is always open: record the bill he
                        * already wrote, which keeps the number.
                        */}
                      {movingBackward ? <p>{t("counters.recordInstead")}</p> : null}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {warnings.length === 0 ? null : (
                <Alert variant="destructive">
                  <TriangleAlertIcon />
                  <AlertTitle>{t("counters.warningsTitle")}</AlertTitle>
                  <AlertDescription>
                    <div className="flex flex-col gap-2">
                      <p>{t("counters.warningsIntro")}</p>
                      <ul className="list-disc pl-4">
                        {warnings.map((warning) => (
                          <li key={warning}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/*
            * The direction rule is the API's, restated so the 400 never
            * surprises: the OWNER may only move counters FORWARD (§6.1 — past
            * bills he wrote by hand); moving one backward risks re-issuing a
            * number that exists on paper, so it needs the ADMIN.
            *
            * ASKED AS A CAPABILITY, NOT AS A ROLE, SINCE 2026-08-16. These two
            * lines read `user?.role === "ADMIN"` until a sweep found them, and
            * CLAUDE.md names that exact expression as the thing never to write in
            * a component — it is why adding ACCOUNTANT later becomes a hunt
            * through the UI rather than one edit to the map.
            *
            * `canSetCounter` is the SAME function the API decides with
            * (permissions.ts), so the sentence shown here cannot drift from the
            * 400 it is warning about. Asking "may this person move one BACKWARD"
            * is also the honest question: the answer happens to be ADMIN today,
            * and the screen no longer cares which role that is.
            */}
          <Alert>
            {/*
             * INFORMATION, NOT WARNING — and the icon is what says so at a
             * glance. This box explains which directions the counter may move;
             * it is not reporting that anything is wrong. `variant="destructive"`
             * would be the wrong shape here, and an unadorned box reads as a
             * paragraph the eye skips. Same `InfoIcon` the credit-note screens
             * use, so an explanatory box looks the same wherever it appears.
             */}
            <InfoIcon />
            <AlertTitle>
              {mayMoveBackward ? t("counters.adminDirection") : t("counters.ownerDirection")}
            </AlertTitle>
            <AlertDescription>
              {mayMoveBackward
                ? t("counters.adminDirectionWhy")
                : t("counters.ownerDirectionWhy")}
            </AlertDescription>
          </Alert>

          {/*
            * EACH HELPER STATES THE NUMBER THE APP IS ON RIGHT NOW — and until
            * 2026-08-17 none of them did.
            *
            * `counters.nowInvoice` and `counters.nowQuote` were called with
            * `{ seq, fy }` against strings that contained NO placeholder ("Next
            * bill number"), in both locales. i18next dropped the values in
            * silence, so the helper rendered a second copy of the label above it
            * and the one fact this screen exists to show — what the counter IS —
            * was nowhere on it. The owner read the duplication as a typo; it was
            * the data going missing.
            *
            * The tests assert a REAL NUMBER is on screen for exactly that
            * reason: an assertion against the translated string would pass again
            * the day the placeholder is dropped.
            */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nextInvoiceSeq">{t("counters.newInvoiceSeq")}</Label>
              <Input
                id="nextInvoiceSeq"
                inputMode="numeric"
                {...form.register("nextInvoiceSeq", { valueAsNumber: true })}
              />
              <p className="text-muted-foreground text-xs">
                {t("counters.nowInvoice", { seq: company.nextInvoiceSeq, fy: company.currentFy })}
              </p>
              <FieldError message={form.formState.errors.nextInvoiceSeq?.message} />
              {/* The "numbers marked below" the refusal above asks him to check. */}
              {isLowered("nextInvoiceSeq") ? (
                <p className="text-destructive text-xs">{t("counters.onlyForwardField")}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nextQuoteSeq">{t("counters.newQuoteSeq")}</Label>
              <Input
                id="nextQuoteSeq"
                inputMode="numeric"
                {...form.register("nextQuoteSeq", { valueAsNumber: true })}
              />
              {/*
                * NO `fy` PASSED, because the quotation series has no marker to
                * show: `QOT/542/26` never resets, and the `/26` comes from the
                * document's own date (DOCUMENT_SERIES_COUNTERS gives quotations
                * `fy: null`). Handing this string an `fy` would put a year on
                * screen that nothing in the app reads.
                */}
              <p className="text-muted-foreground text-xs">
                {t("counters.nowQuote", { seq: company.nextQuoteSeq })}
              </p>
              <FieldError message={form.formState.errors.nextQuoteSeq?.message} />
              {isLowered("nextQuoteSeq") ? (
                <p className="text-destructive text-xs">{t("counters.onlyForwardField")}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="nextChallanSeq">{t("counters.newChallanSeq")}</Label>
              <Input
                id="nextChallanSeq"
                inputMode="numeric"
                {...form.register("nextChallanSeq", { valueAsNumber: true })}
              />
              <p className="text-muted-foreground text-xs">
                {t("counters.nowChallan", { seq: company.nextChallanSeq, fy: company.challanFy })}
              </p>
              <FieldError message={form.formState.errors.nextChallanSeq?.message} />
              {isLowered("nextChallanSeq") ? (
                <p className="text-destructive text-xs">{t("counters.onlyForwardField")}</p>
              ) : null}
            </div>
          </div>
          {/*
           * ═══════════════════════════════════════════════════════════════════
           * THERE IS NO FINANCIAL-YEAR CONTROL ON THIS FORM, FOR ANY SERIES.
           *
           * A "New challan year" input existed here for about an hour on
           * 2026-08-17 and was removed the same evening. The marker rolls
           * ITSELF: `allocateDocumentNumber` compares the stored label with the
           * financial year of the document being raised and, when they differ,
           * writes the new label and resets the sequence to 1 in the same
           * `FOR UPDATE` transaction (services/numbering.ts).
           *
           * So the box could only ever do one of two things, and neither was
           * wanted. Typing the year it already is does nothing. Typing any other
           * year is actively harmful: `assertNotBackdated` refuses to allocate
           * when the stored label is AHEAD of the document's own year, so a
           * finger-slip into a future year would 409 EVERY CHALLAN until
           * somebody worked out which screen had done it.
           *
           * The invoice and quotation series never exposed theirs either, so
           * this also removes the odd one out. Nothing on this screen writes any
           * `*_fy` column now, which is the strongest possible version of the
           * rule that `challan_fy` and `current_fy` must never be confused.
           * ═══════════════════════════════════════════════════════════════════
           */}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="counterReason">{t("counters.reason")}</Label>
            <Input id="counterReason" {...form.register("reason")} />
            <p className="text-muted-foreground text-xs">{t("counters.reasonHelp")}</p>
            <FieldError message={form.formState.errors.reason?.message} />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="counterConfirm"
              checked={confirm === true}
              onCheckedChange={(checked) => {
                form.setValue("confirm", checked as true, { shouldValidate: true })
              }}
            />
            <Label htmlFor="counterConfirm">{t("counters.confirm")}</Label>
          </div>

          <div className="flex justify-end">
            <Button type="submit" variant="destructive" disabled={save.isPending || confirm !== true}>
              {save.isPending ? <Loader2Icon className="animate-spin" /> : null}
              {t("counters.submit")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
